// import debounce from "lodash/debounce"
import { RefCallback, useCallback, useMemo, useRef, useState } from "react"
import isEqual from "react-fast-compare"
import { useMountedState } from "react-use"
import ResizeObserver from "resize-observer-polyfill"

// These are the keys of the bounding client rect object which the size hook
// will track in state.
type RectKey = "width" | "height" | "top" | "left" | "right" | "bottom"
// This is the value of the bounding client rect object derived from the keys.
type RectValue<Key extends RectKey> = Record<Key, number>

// When no keys are provided, the default keys will be used.
const defaultRectKeys: RectKey[] = [
  "width",
  "height",
  "top",
  "left",
  "right",
  "bottom"
]

// The useSize hook returns a tuple where the 2nd item is either a single number value or
// an object containing all the keys provided to the hook with their values.
type SingleKeyReturn = number
type MultipleKeyReturn<K extends RectKey> = RectValue<K>
type RectValueReturn<K extends RectKey | RectKey[]> = K extends RectKey
  ? SingleKeyReturn
  : K extends RectKey[]
  ? MultipleKeyReturn<K[number]>
  : never
type UseSizeHookReturn<K extends RectKey | RectKey[] = RectKey> = [
  RefCallback<HTMLElement>,
  RectValueReturn<K>,
  { current: HTMLElement | null }
]

// Observe the size of a DOM node.
export function useSize<Key extends RectKey>(keys: Key): UseSizeHookReturn<Key>
export function useSize<Key extends RectKey>(
  keys?: Key[]
): UseSizeHookReturn<Key[]>
export function useSize<Key extends RectKey>(
  keys: Key | Key[] = defaultRectKeys as Key[]
): [
  UseSizeHookReturn[0],
  SingleKeyReturn | MultipleKeyReturn<Key>,
  UseSizeHookReturn[2]
] {
  const isSingleKey = typeof keys === "string"
  // Transform the keys into an array if a single key is provided.
  const keysArray = Array.isArray(keys) ? keys : [keys]

  const isMounted = useMountedState()
  const observerRef = useRef<ResizeObserver>()

  // Scope the provided rect value to the keys.
  // Default all missing keys to 0 value.
  function getRectValue(rectValue: RectValue<RectKey>) {
    if (isSingleKey) {
      // Return just the number value for the single key
      const key = keys as RectKey
      return rectValue[key] || 0
    }
    // Return object for multiple keys
    return keysArray.reduce((obj, key) => {
      obj[key] = rectValue[key] || 0
      return obj
    }, {} as RectValue<Key>)
  }

  const initialRectValue = useMemo(
    () => getRectValue({} as RectValue<RectKey>),
    [...keysArray]
  )
  const [rectValue, setRectValue] = useState(initialRectValue)
  const rectValueRef = useRef(rectValue)

  // Update both state and ref with the new rect value.
  function handleUpdateRectValue(rectValue: RectValue<Key> | number) {
    rectValueRef.current = rectValue
    setRectValue(rectValue)
  }

  // Updates the rect value in state based on the target element's
  // bounding client rect and the provided keys.
  const updateRectValue = useCallback((target: HTMLElement | Element) => {
    if (target) {
      const boundingClientRect = target.getBoundingClientRect()
      const nextRectValue = getRectValue(boundingClientRect)

      if (!isEqual(nextRectValue, rectValueRef.current)) {
        handleUpdateRectValue(nextRectValue)
        // debouncedUpdateRectValue(target)
      }
    }
  }, [])

  // // NOTE: in some edge cases, the resize observer (whether pollyfilled or not)
  // // does not observe the target element's final size when multiple resize events
  // // occur immediately after the component mounts. This debounced fn should
  // // hopefully correctly set the latest rect values for the target element.
  // const debouncedUpdateRectValue = useCallback(
  //   debounce(updateRectValue, 100),
  //   []
  // )

  // Use a callback ref to respond to ref updates.
  // Connect/disconnect the observer the target element.

  const nodeRef = useRef<HTMLElement | null>(null)
  const setRef = useCallback<RefCallback<HTMLElement>>((node) => {
    // Update the ref's current value
    nodeRef.current = node

    // Set up new observer if we have a node
    if (node) {
      observerRef.current = new ResizeObserver((entries) => {
        const { target } = entries[0]
        // Explicitly check for mounted state.
        if (isMounted()) updateRectValue(target)
      })

      observerRef.current.observe(node)
    } else {
      // Clean up existing observer
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
      handleUpdateRectValue(getRectValue({} as RectValue<RectKey>))
    }
  }, [])

  return [setRef, rectValue, nodeRef]
}
